/**
 * What the SERVER knows about the call that is up, and the three actions that
 * need the server rather than the Voice SDK.
 *
 * Its own file rather than more state inside CallProvider, which was already at
 * 482 lines before any of this: a poll, a reconcile effect, three network
 * actions and three refs would put it well past the point where the module's
 * own rules say to split. CallProvider keeps the Device and the call lifecycle;
 * this owns everything the browser cannot work out on its own.
 *
 * ⚠ THE POLL RUNS ON EVERY CALL, not only on conferences. It was conference-only
 * when it was written, because hold was the only thing it answered. That turned
 * out to be the shape of a much worse bug: with `twilio.conference` off - which
 * is every call this installation actually places - the browser's ONLY source of
 * "the call has ended" was the Voice SDK's `disconnect` event, and that event
 * travels over the signalling websocket. When the access token lapses, Twilio
 * refuses the SDK's reconnect with 20101 and every send after it fails with
 * 31009, while the WebRTC audio carries on regardless. The result, reported from
 * production on 2026-08-11: the far end hangs up and the CRM sits there showing
 * a live call, and the Hang up button does nothing at all. `status` below comes
 * from Twilio's own status callback and needs nothing of the browser.
 *
 * Four things the browser can no longer work out on its own, all answered by one
 * cheap poll:
 *
 *  1. ENDED. See above. The authority on this is Twilio, not the websocket.
 *  2. HOLD. There is no SDK event for it - unlike mute, which the Voice SDK
 *     reports back - so a remount, or a second tab, would otherwise show a held
 *     call as live.
 *  3. BRIDGED. `answerOnBridge` has no conference equivalent, so
 *     `call.on('accept')` now fires the instant the browser joins an EMPTY
 *     room. The customer entering it is a server-side event and nothing in the
 *     browser can see it. Note this is NOT the server's `answeredAt` either:
 *     that is written when their phone is picked up, several seconds before the
 *     announcement finishes.
 *  4. RECORDING. The recording is a separate REST call that can fail on its
 *     own, after the customer was already told the call is recorded. The red
 *     dot has to follow what happened, not what was promised.
 *
 * Points 2-4 are conference-only and read as harmless constants on a `<Dial>`
 * call (no hold, never bridged, recording straight from the row), which is why
 * one poll can serve both bridges without a branch.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  abandonBridge,
  cancelCallTransfer,
  getCallBridge,
  hangupCallServerSide,
  setCallHold,
  stopCallRecording,
  transferCall,
  type CallBridgeState,
  type CallTransferState,
  type RecordingStopBlock,
  type RecordingStopReason,
  type TransferBlock,
  type TransferMode,
} from "../../lib/calls";

/** Slow enough to be invisible on the bill, fast enough that hold feels live. */
const POLL_MS = 2000;

/**
 * Nothing left to watch - the call is over however it ended.
 *
 * `blocked` is ours rather than Twilio's, and a call carrying it should never
 * reach this poll: it is refused at the inbound webhook before any browser leg
 * exists. Listed anyway, because the cost of being wrong is not symmetric. One
 * string here is free; a status this set does not know is a poll that never
 * stops, every two seconds, for as long as the tab is open.
 */
export const TERMINAL = new Set(["completed", "busy", "no-answer", "failed", "canceled", "blocked"]);

/** The slice of call state this hook reads and writes. */
export interface BridgeSlice {
  phase: string;
  callLogId: string | null;
  conference: boolean;
  hold: boolean;
  holdPending: boolean;
  bridged: boolean;
  recording: boolean;
  /** Why the recording cannot be stopped by hand, from the poll. */
  recordingStopBlocked: RecordingStopBlock;
  /** A stop request is in flight. */
  recordingStopPending: boolean;
  /** The stop landed, and whether Twilio's copy went with it. */
  recordingStopped: { destroyed: boolean } | null;
  /** The last stop attempt's own refusal. */
  recordingStopError: string | null;
  answeredAt: number | null;
  error: string | null;
  /** The third party on this call, from the poll. Null when there is nobody. */
  transfer: CallTransferState | null;
  /** Why nobody can be invited right now, from the poll. */
  transferBlocked: TransferBlock;
  /** A transfer request is in flight. Like hold, this one is a network round trip. */
  transferPending: boolean;
  /** The last transfer attempt's own error code, shown beside the control. */
  transferError: string | null;
  /** The row's own status, verbatim. See `CallState.serverStatus`. */
  serverStatus: string;
}

export interface CallBridgeControls {
  toggleHold: () => void;
  /**
   * End the call through the server. Fire-and-forget alongside the SDK's own
   * `disconnect()`, and the half that still works when the websocket does not.
   *
   * Also carries the ghost-leg guard that `abandonIfUnbridged` used to fire
   * separately - see the implementation for why one request beats two.
   */
  hangupServerSide: () => void;
  /** Ring a third party into the room. */
  startTransfer: (to: string, mode: TransferMode) => void;
  /** Take them off again, or call the whole thing off. */
  stopTransfer: () => void;
  /**
   * Stop recording this call without ending it, because somebody asked.
   *
   * The one control here that is NOT optimistic - see the implementation.
   */
  stopRecording: (reason: RecordingStopReason) => void;
}

export interface CallBridgeOptions<S extends BridgeSlice> {
  state: S;
  patch: (fn: (prev: S) => S) => void;
  /**
   * Twilio says the call is over and the browser had not noticed.
   *
   * A callback rather than another field in the patch above, because ending a
   * call is more than a phase: the `Call` object has to be let go and the
   * `Device` released, and both of those live in CallProvider.
   *
   * `durationSec` is Twilio's own count, used when the browser never saw the
   * call answered and therefore has no moment of its own to measure from.
   */
  onServerEnded: (durationSec: number | null) => void;
}

export function useCallBridge<S extends BridgeSlice>(
  opts: CallBridgeOptions<S>,
): CallBridgeControls {
  const { state, patch, onServerEnded } = opts;
  const { phase, callLogId, conference } = state;
  const active = phase === "connecting" || phase === "ringing" || phase === "active";

  // `patch` is recreated every render by the caller's setState wrapper, and the
  // poll must not restart because of that. Same for the two values the hangup
  // path reads: it fires on a click that may precede a render.
  const patchRef = useRef(patch);
  const endedRef = useRef(onServerEnded);
  const bridgedRef = useRef(state.bridged);
  const holdPendingRef = useRef(state.holdPending);
  useEffect(() => {
    patchRef.current = patch;
    endedRef.current = onServerEnded;
    bridgedRef.current = state.bridged;
    holdPendingRef.current = state.holdPending;
  });

  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!callLogId || !active) return;
    let cancelled = false;
    let stopped = false;

    async function poll() {
      if (cancelled || stopped || !callLogId) return;
      let s: CallBridgeState;
      try {
        s = await getCallBridge(callLogId);
      } catch {
        // Keep the last known values. A dropped poll during a live call is a
        // network blip, and flipping the button back would be a lie about the
        // customer's actual state.
        return;
      }
      if (cancelled) return;
      if (TERMINAL.has(s.status)) {
        // Twilio has the last word on this, and it is the last word: nothing
        // below is worth patching onto a call that is over, and the window is
        // about to leave the live phases anyway.
        stopped = true;
        endedRef.current(s.durationSec);
        return;
      }
      patchRef.current((prev) => {
        // Not while a request is in flight: the optimistic value is newer than
        // a poll that was already on the wire when the button was pressed.
        const hold = holdPendingRef.current ? prev.hold : s.hold;
        // The `answerOnBridge` replacement: the first tick that says the
        // customer is in the room is the first moment the two of them can hear
        // each other, and therefore where the talk timer starts.
        const promote = s.bridged && prev.phase !== "active";
        // Compared field by field rather than by identity, for the same reason
        // the three above are: the server sends a fresh object every tick, so
        // `s.transfer !== prev.transfer` is true forever and would defeat the
        // bail-out below on every call that has a third party on it.
        const sameTransfer =
          (s.transfer?.number ?? null) === (prev.transfer?.number ?? null) &&
          (s.transfer?.status ?? null) === (prev.transfer?.status ?? null) &&
          (s.transfer?.joined ?? null) === (prev.transfer?.joined ?? null) &&
          (s.transfer?.mode ?? null) === (prev.transfer?.mode ?? null);
        // THE SAME OBJECT WHEN NOTHING MOVED, so React's `useState` can bail
        // out of the render. This matters now that the poll runs on every call
        // rather than on conferences alone: on a `<Dial>` call all of these are
        // constants for its whole life (no hold, never bridged, nobody to
        // invite, recording straight off the row), so every tick was a new state
        // object, a new context value and a re-render of every consumer of it -
        // twice a second, for nothing, for the length of the conversation.
        if (
          !promote &&
          hold === prev.hold &&
          s.bridged === prev.bridged &&
          s.recording === prev.recording &&
          s.recordingStopBlocked === prev.recordingStopBlocked &&
          sameTransfer &&
          s.transferBlocked === prev.transferBlocked &&
          // In the comparison as well as in the patch, or the bail-out below
          // would keep returning `prev` and the status would never move off ""
          // - which on a mobile call is the difference between telling the
          // operator to answer their phone and telling them nothing.
          s.status === prev.serverStatus
        ) {
          return prev;
        }
        return {
          ...prev,
          serverStatus: s.status,
          hold,
          bridged: s.bridged,
          // `false` is how the red dot disappears when the leg recording failed
          // to start - something the <Dial> path could never report.
          recording: s.recording,
          // Not protected by a pending flag the way `hold` is above, and it does
          // not need to be: a stop is one-way. Once the server says
          // "already_stopped" no later poll can say anything else, so a tick
          // that was already on the wire cannot put the button back.
          recordingStopBlocked: s.recordingStopBlocked,
          // Straight from the server on every tick, including back to null when
          // the third party hangs up. There is no optimistic value to protect
          // here the way there is for hold: the request that starts a transfer
          // returns the same shape and writes it itself.
          transfer: s.transfer,
          transferBlocked: s.transferBlocked,
          ...(promote ? { phase: "active", answeredAt: prev.answeredAt ?? Date.now() } : {}),
        };
      });
    }

    void poll();
    const timer = setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // `tick` restarts the poll after a hold, so the confirming state is picked
    // up straight away rather than up to POLL_MS later.
  }, [callLogId, active, tick]);

  /**
   * Hold the customer, or bring them back.
   *
   * Unlike mute there is NO SDK event to correct this the way `call.on('mute')`
   * corrects mute, so the button owns both its optimism and its rollback - and
   * a failed hold is a failed BUTTON, not a failed call: `hold` is left where it
   * was and the phase is not touched, because the two of them are still talking.
   */
  const toggleHold = useCallback(() => {
    if (!callLogId || !conference) return;
    patchRef.current((prev) => {
      if (prev.holdPending) return prev;
      const next = !prev.hold;
      void setCallHold(callLogId, next)
        .then((res) => {
          patchRef.current((s) => ({ ...s, hold: res.hold, holdPending: false }));
          setTick((n) => n + 1);
        })
        .catch(() => {
          patchRef.current((s) => ({ ...s, holdPending: false, error: "hold_failed" }));
        });
      // Clear the previous failure as the retry starts, so the warning below
      // the buttons belongs to this attempt and not to the last one.
      return { ...prev, holdPending: true, error: prev.error === "hold_failed" ? null : prev.error };
    });
  }, [callLogId, conference]);

  /**
   * Ring a third party into the room.
   *
   * The answer is written from the RESPONSE rather than optimistically, unlike
   * hold. Hold is a state the operator already knows the outcome of - they
   * pressed it - but a transfer is a phone that has to start ringing somewhere
   * else, and drawing a third party onto the call before the server has managed
   * to dial one would show a name that then vanished. What is optimistic is only
   * `transferPending`, which is what stops a second click.
   *
   * Refusals arrive as an ApiError whose message is the server's own code, and
   * they are kept: "this number is on the do-not-call list" and "that is the
   * number you are already talking to" are different mistakes with different
   * fixes, and a generic failure would send the operator to check their typing.
   */
  const startTransfer = useCallback(
    (to: string, mode: TransferMode) => {
      if (!callLogId || !conference) return;
      patchRef.current((prev) => {
        if (prev.transferPending) return prev;
        void transferCall(callLogId, to, mode)
          .then((res) => {
            patchRef.current((s) => ({
              ...s,
              transfer: res.transfer,
              transferPending: false,
              transferError: null,
            }));
            setTick((n) => n + 1);
          })
          .catch((err: unknown) => {
            const code = err instanceof Error && err.message ? err.message : "transfer_failed";
            patchRef.current((s) => ({ ...s, transferPending: false, transferError: code }));
          });
        return { ...prev, transferPending: true, transferError: null };
      });
    },
    [callLogId, conference],
  );

  /**
   * Take the third party off, or call the whole thing off before they answer.
   *
   * The failure here matters more than most: on a handover, this request is
   * also what gives the operator's own leg back its power to end the room. A
   * failed cancel therefore leaves the error on screen rather than clearing it
   * quietly - the operator has to know that hanging up may not be enough.
   */
  const stopTransfer = useCallback(() => {
    if (!callLogId) return;
    patchRef.current((prev) => {
      if (prev.transferPending || !prev.transfer) return prev;
      void cancelCallTransfer(callLogId)
        .then(() => {
          patchRef.current((s) => ({
            ...s,
            transfer: null,
            transferPending: false,
            transferError: null,
          }));
          setTick((n) => n + 1);
        })
        .catch((err: unknown) => {
          const code = err instanceof Error && err.message ? err.message : "transfer_failed";
          patchRef.current((s) => ({ ...s, transferPending: false, transferError: code }));
        });
      return { ...prev, transferPending: true, transferError: null };
    });
  }, [callLogId]);

  /**
   * Stop recording, because the person on the phone asked us to.
   *
   * THE ONE CONTROL HERE THAT IS NOT OPTIMISTIC, and that is the whole design.
   * Hold shows its new state immediately because a failed hold is a failed
   * button and the two of them carry on talking either way. This is a promise
   * being kept: putting the red dot out before the server has confirmed would
   * tell the operator - who will then tell the customer - that the recording has
   * stopped when it may not have. The dot stays lit until the answer comes back.
   *
   * `destroyed: false` is not a failure and is not treated as one. The recording
   * has stopped; what may survive is Twilio's copy of the part already captured,
   * which our own callback refuses to download and the orphan report can clear.
   * The window says the stop worked and mentions the leftover, rather than
   * showing an error for something that did happen.
   */
  const stopRecording = useCallback(
    (reason: RecordingStopReason) => {
      if (!callLogId) return;
      patchRef.current((prev) => {
        if (prev.recordingStopPending || prev.recordingStopped) return prev;
        void stopCallRecording(callLogId, reason)
          .then((res) => {
            patchRef.current((s) => ({
              ...s,
              recordingStopPending: false,
              recordingStopped: { destroyed: res.destroyed },
              // The dot goes out here rather than waiting for the next poll:
              // the server has confirmed, and up to two seconds of a red dot
              // after the operator has told somebody it stopped is two seconds
              // of the CRM contradicting them out loud.
              recording: false,
              recordingStopBlocked: "already_stopped",
              recordingStopError: null,
            }));
            setTick((n) => n + 1);
          })
          .catch((err: unknown) => {
            const code = err instanceof Error && err.message ? err.message : "stop_failed";
            patchRef.current((s) => ({
              ...s,
              recordingStopPending: false,
              recordingStopError: code,
            }));
          });
        return { ...prev, recordingStopPending: true, recordingStopError: null };
      });
    },
    [callLogId],
  );

  const hangupServerSide = useCallback(() => {
    if (!callLogId) return;

    /**
     * The ghost-leg guard, as a FALLBACK rather than a second request.
     *
     * A conference customer who has not joined is an independent call created
     * by `POST /Calls`, and ending the room does nothing to a phone still
     * ringing outside it. `/hangup` already cancels that leg before it touches
     * the room - the same two calls in the same order as `/bridge/end` - so
     * firing both from one click raced two copies of identical work against the
     * same sids, and the loser logged "cancelling the customer leg failed" for
     * a teardown that had in fact just succeeded.
     */
    const abandon = () => {
      if (!conference || bridgedRef.current) return;
      void abandonBridge(callLogId).catch(() => {});
    };

    void hangupCallServerSide(callLogId)
      // Errors swallowed on purpose. The SDK's `disconnect()` runs beside this
      // one and the window closes on its own either way; an operator who has
      // already pressed Hang up has nothing to do with a failure here, and the
      // server logs the Twilio side of it.
      .then((res) => {
        // The one case `/hangup` declines to touch Twilio at all: the row is
        // already terminal. On a conference that status belongs to the
        // CUSTOMER's leg, so it says nothing about a room the operator may
        // still be sitting in - which is exactly what `bridge/end` is for.
        if (!res.ended) abandon();
      })
      .catch(() => {
        // The request never landed. Fall back to the older path, which at least
        // stops a stranger's phone ringing.
        abandon();
      });
  }, [callLogId, conference]);

  return { toggleHold, hangupServerSide, startTransfer, stopTransfer, stopRecording };
}
